import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  MultiLanguageSelect,
  IconGreetingHand
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';
import SearchLinks from '../SearchLinks/SearchLinks';
import AuthMenu from './AuthMenu/AuthMenu';

import css from './TopbarDesktop.module.css';

const draftId = '00000000-0000-0000-0000-000000000000';

const ProfileMenu = ({
  currentPage,
  currentUser,
  onLogout,
  showManageListingsLink,
  showCreateListingsLink,
  authenticatedOnClientSide,
  inboxTab,
  notificationCount,
  isAdmin,
  showTranslationsLink,
}) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  const currentUserFirstName = currentUser?.attributes?.profile?.firstName

  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <div className={css.profileMenuButton}>
          <IconGreetingHand/>
          <FormattedMessage id="TopbarDesktop.greeting" values={{firstName: currentUserFirstName}} />
        </div>
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {authenticatedOnClientSide ? (
          <MenuItem key="Inbox">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('InboxPage'))}
              name="InboxPage"
              params={{ tab: inboxTab }}
            >
              <span className={css.menuItemBorder} />
              <span className={css.inboxLinkLabelMenu}>
                <FormattedMessage id="TopbarDesktop.inbox" />
                {notificationDot}
              </span>
            </NamedLink>
          </MenuItem>
        ) : null}
        {showCreateListingsLink ? (
          <MenuItem key="NewListingPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('NewListingPage'))}
              name="NewListingPage"
              params={{ slug: 'draft', id: draftId, type: 'new', tab: 'details' }}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.createListing" />
            </NamedLink>
          </MenuItem>
        ) : null}
        {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null}

        <MenuItem key="FavouriteListingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('FavouriteListingsPage'))}
            name="FavouriteListingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.favouriteListingsLink" />
          </NamedLink>
        </MenuItem>

        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>

        {isAdmin ? (
          <MenuItem key="RewildingInfoPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('RewildingInfoPage'))}
              name="RewildingInfoPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.rewildingInfoLink" />
            </NamedLink>
          </MenuItem>
        ) : null}

        {showTranslationsLink ? (
          <MenuItem key="TranslationsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('TranslationsPage'))}
              name="TranslationsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarMobileMenu.translationsPage" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
    isUseCustomFullMenu,
    onLandscapesSection,
    rewildingLandscapesSection,
    isAdmin,
    topbarFixed,
    listingsByFilters
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className, topbarFixed && css.rootFixed);

  const showTranslationsLink = currentUser?.attributes?.profile?.protectedData?.isAdmin;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
      showCreateListingsLink={showCreateListingsLink}
      authenticatedOnClientSide={authenticatedOnClientSide}
      inboxTab={inboxTab}
      notificationCount={notificationCount}
      isAdmin={isAdmin}
      showTranslationsLink={showTranslationsLink}
    />
  ) : null;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );

  return (
    <nav className={classes}>
      <LinkedLogo
        className={css.logoLink}
        layout="desktop"
        alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        linkToExternalSite={config?.topbar?.logoLink}
      />
      {searchFormMaybe}

      <SearchLinks config={config} />

      <div className={css.rightSide}>
        <div className={css.customLinksWrapper}>
          <CustomLinksMenu
            currentPage={currentPage}
            customLinks={customLinks}
            intl={intl}
            hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
            isUseCustomFullMenu={isUseCustomFullMenu}
            onLandscapesSection={onLandscapesSection}
            rewildingLandscapesSection={rewildingLandscapesSection}
            listingsByFilters={listingsByFilters}
          />
          {profileMenuMaybe}
          {!isAuthenticatedOrJustHydrated && <AuthMenu intl={intl} />}
        </div>
        <MultiLanguageSelect />
      </div>
    </nav>
  );
};

export default TopbarDesktop;
